/* =============================================================================
 * What a figure on a screen is a figure *of*
 * -----------------------------------------------------------------------------
 * Every number in this application came from somewhere. 44.35 overtime hours is
 * thirty-one days each with a story; AED 653.05 recovered is an advance with a
 * date and a balance; a name on a payroll table is a person with a file. The
 * screens showed the answer and made the reader go and find the working.
 *
 * A drill target is that link, named once. `{ kind: 'attendanceDay', employeeId,
 * workDate }` says what a cell stands for, and everything else — which panel
 * opens, which permission it needs, which page it links on to — is decided from
 * that in one place rather than at two hundred call sites.
 *
 * -----------------------------------------------------------------------------
 * WHY THE PERMISSION LIVES HERE
 * -----------------------------------------------------------------------------
 * A site supervisor sees their crew and never sees pay. If a payroll figure on a
 * shared table were made clickable without asking, the supervisor would get a
 * link that opens a panel the database then refuses — an error message where
 * there should have been nothing at all. Worse, the link itself tells them a
 * figure exists.
 *
 * So a target declares what it needs, `<Drill>` checks before it renders
 * anything clickable, and a reader without the grant sees plain text and no
 * hint that anybody else sees more. RLS still refuses underneath; this is the
 * half that keeps the interface honest.
 * ========================================================================== */

/** One thing a reader can open the detail of. */
export type DrillTarget =
  | { kind: 'attendanceDay'; employeeId: string; workDate: string; name?: string }
  | { kind: 'employee'; employeeId: string; name?: string }
  | { kind: 'advances'; employeeId: string; name?: string }
  | { kind: 'project'; projectId: string; name?: string }
  | { kind: 'leave'; leaveId: string; name?: string };

export type DrillKind = DrillTarget['kind'];

/**
 * The grant a reader needs before a target is offered to them at all.
 *
 * `null` means anybody signed in may look — a project's own details carry no
 * personal or pay information.
 *
 * `selfIsEnough` is what makes the employee portal work: a person may always
 * open their own day, their own payslip and their own advances, and holds none
 * of these grants. The check is against the employee id on the target, so it
 * cannot accidentally admit somebody to a colleague's figures.
 */
export const NEEDS: Record<DrillKind, { grant: string | null; selfIsEnough: boolean }> = {
  attendanceDay: { grant: 'attendance.view', selfIsEnough: true },
  employee: { grant: 'employees.view', selfIsEnough: true },
  advances: { grant: 'advances.view', selfIsEnough: true },
  project: { grant: null, selfIsEnough: false },
  /* No employeeId on the target, so selfIsEnough cannot help here and is
     false: a leave request is opened from a screen that already required the
     grant to reach. The employee's own portal shows his leave from his own
     rows rather than through a panel. */
  leave: { grant: 'leave.view', selfIsEnough: false },
};

/** The employee a target is about, where it is about one. */
export function subjectOf(target: DrillTarget): string | null {
  return 'employeeId' in target ? target.employeeId : null;
}

/**
 * Whether this reader may open this target.
 *
 * Takes the two facts rather than the session, so it can be tested as what it
 * is — a rule — without a provider, a router or a signed-in user.
 */
export function mayOpen(
  target: DrillTarget,
  can: (permission: string) => boolean,
  myEmployeeId: string | null,
): boolean {
  const rule = NEEDS[target.kind];
  if (rule.grant === null) return true;
  if (can(rule.grant)) return true;

  if (!rule.selfIsEnough) return false;

  const subject = subjectOf(target);
  return subject !== null && myEmployeeId !== null && subject === myEmployeeId;
}

/**
 * The page this target lives on in full, for the link at the foot of a panel.
 *
 * A panel answers the question in front of somebody; the page is where they go
 * to act on it. Both, always — the panel so they do not lose their place, the
 * link so they are not trapped in a summary.
 */
export function pageFor(target: DrillTarget): string {
  switch (target.kind) {
    case 'attendanceDay':
      return `/attendance/${target.employeeId}/${target.workDate}`;
    case 'employee':
      return `/employees/${target.employeeId}`;
    case 'advances':
      return `/advances?employee=${target.employeeId}`;
    case 'project':
      return `/projects/${target.projectId}`;
    case 'leave':
      return `/leaves/${target.leaveId}`;
    default:
      return '/';
  }
}

/** A stable key, so opening the same thing twice does not stack two panels. */
export function keyOf(target: DrillTarget): string {
  switch (target.kind) {
    case 'attendanceDay':
      return `attendanceDay:${target.employeeId}:${target.workDate}`;
    case 'employee':
      return `employee:${target.employeeId}`;
    case 'advances':
      return `advances:${target.employeeId}`;
    case 'project':
      return `project:${target.projectId}`;
    case 'leave':
      return `leave:${target.leaveId}`;
    default:
      return 'unknown';
  }
}
